/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import { v4 as uuidv4 } from 'uuid'
import { PRODUCT_SEARCH_QUERY } from '../../api/queries'
import { productSearch, executeGenderFallbackSearch } from '../../api/request'

const liveSearchProductsRequest = {
    /**
     * Fetch and process products from the catalog service
     *
     * @param {string} type - The type of product fetch ("next", "previous", "gender" or "initial")
     * @param {number} pagination - The page number to fetch
     * @returns {Promise<void>}
     */
    async getProducts(type = 'next', pagination = 1) {
        try {
            const requestedPage = Math.max(parseInt(pagination, 10) || 1, 1)

            // Set loading state and fetch products
            this.productsLoading = true
            this.productFetchErrorMessage = ''
            this.lastProductRequest = { type, pagination: requestedPage }

            this.searchRequestId = uuidv4()

            this.onSearchRequest({
                searchTerm: this.searchPhrase,
                filters: this.createFilter(type),
                sort: this.getSortOptions(),
                page: requestedPage,
                pageSize: this.pagesSize,
                searchRequestId: this.searchRequestId,
                searchUnitId: this.view === 'category' ? 'plp' : 'search'
            })
            const { response: result, genderMeta } = await this.getCatalogProducts(type, requestedPage)

            const shouldReloadWithUrlFacets = this.applyPendingUrlFacetsFromAllowlist(
                result.productSearch.facets
            )

            if (shouldReloadWithUrlFacets && !this.hasTriggeredUrlFacetReload) {
                this.hasTriggeredUrlFacetReload = true
                this.setUrlPageParam(1)
                this.resetProducts()
                await this.getProducts('facets', 1)
                return
            }

            this.applyFallbackHeading(genderMeta)

            if (!result?.productSearch) {
                throw new Error('Live Search response is missing productSearch data.')
            }

            if (await this.recoverFromOutOfRangePage(requestedPage, result)) {
                return
            }

            this.onSearchResponse({
                searchTerm: this.searchPhrase,
                filters: result.productSearch.facets,
                sort: this.getSortOptions(),
                page: result.productSearch?.page_info?.current_page,
                pageSize: result.productSearch?.page_info?.page_size,
                items: result.productSearch.items,
                total: result.productSearch.total_count,
                searchRequestId: this.searchRequestId,
                searchUnitId: this.view === 'category' ? 'plp' : 'search'
            })

            // Remove skeleton loaders on first load
            if (!this.productsInitialLoaded) {
                this.removeSkeleton()
            }

            // Process the fetched products
            const products = this.mapProducts(result.productSearch.items, requestedPage)
            this.setPageInfo(this, result)

            // Determine how to handle the products based on load type
            const isInitialLoad = !this.productsInitialLoaded
            const isFirstPage = requestedPage === 1 && type !== 'previous'

            // Update products differently based on context
            if (isInitialLoad || isFirstPage) {
                // For initial load or first page: update facets, replace products and push to data layer
                this.sortNumericFacetBuckets(result.productSearch.facets, 'bh_size')
                this.sortBucketsByCustomOrder(result.productSearch.facets, 'm_size', this.facetConfig.customSortOrderAttributes.m_size)
                this.updateFacets(result.productSearch.facets)
                this.loadingPlaceholderSlots = []
                this.products = products

                this.$nextTick(() => {
                    this.dataLayerPushItems(this.products)
                })
            } else {
                // For pagination: merge with existing products
                this.loadingPlaceholderSlots = []
                this.mergeProducts(products, type)
            }

            // Notify listeners that products have loaded
            this.dispatchProductListLoadedEvent(type, requestedPage)

            // Complete initial load setup if needed
            if (isInitialLoad) {
                this.productsInitialLoaded = true
                this.handlePrevNextLinkTags()
            }
            // After first API response, apply URL-based facets if any are present in the allowlist
            this.applyPendingUrlFacetsFromAllowlist(result.productSearch.facets ?? [])
        } catch (error) {
            if (await this.recoverFromOutOfBoundsError(error)) {
                return
            }

            if (!this.productFetchErrorMessage) {
                this.handleProductFetchError(error)
            }
        } finally {
            if (this.loadingPlaceholderTimeoutId) {
                window.clearTimeout(this.loadingPlaceholderTimeoutId)
                this.loadingPlaceholderTimeoutId = null
            }

            this.productsLoading = false
            this.loadingPlaceholderSlots = []
        }
    },
    /**
     * Fetch catalog products with the current search and filter settings
     *
     * @param {string} type
     * @param {number|null} pagination - The page number to fetch
     * @returns {Promise<Object>} Product search results or empty fallback object on error
     */
    async getCatalogProducts(type, pagination = null) {
        try {
            // Build search parameters with current filters and settings
            const searchParams = {
                phrase: this.searchPhrase,
                pageSize: this.pagesSize,
                currentPage: pagination,
                sort: this.getSortOptions()
            }

            // Execute the search query
            const genderMeta = await executeGenderFallbackSearch(async(genderKey) => {
                const params = {
                    ...searchParams,
                    filter: this.createFilter(type, genderKey)
                }

                return await productSearch(
                    this.apiUrl,
                    PRODUCT_SEARCH_QUERY,
                    params,
                    (error) => this.handleProductFetchError(error)
                )
            })

            return {
                response: genderMeta.response ?? this.getEmptyCatalogResponse(pagination),
                genderMeta
            }
        } catch (error) {
            if (error?.isOutOfBoundsPageError) {
                throw error
            }

            console.error('Error fetching catalog products:', error)

            // Return empty result structure to prevent UI errors
            // Full error handling is done in the calling method via handleProductFetchError()
            return {
                response: this.getEmptyCatalogResponse(pagination),
                genderMeta: null
            }
        }
    },
    /**
     * Build empty catalog response
     *
     * @param {number|null} pagination
     * @returns {{productSearch: {facets: *[], total_count: number, page_info: {total_pages: number, page_size: number, current_page: number}, items: *[]}}}
     */
    getEmptyCatalogResponse(pagination = null) {
        return {
            productSearch: {
                facets: [],
                items: [],
                total_count: 0,
                page_info: {
                    current_page: pagination || 1,
                    page_size: this.pagesSize,
                    total_pages: 0
                }
            }
        }
    },
    /**
     * Recover from a stale URL page that no longer exists.
     *
     * @param {number} requestedPage
     * @param {Object} result
     * @returns {Promise<boolean>}
     */
    async recoverFromOutOfRangePage(requestedPage, result) {
        const totalPages = Number(result?.productSearch?.page_info?.total_pages) || 0

        if (requestedPage <= 1 || totalPages < 1 || requestedPage <= totalPages) {
            return false
        }

        const recoveryPage = Math.max(totalPages, 1)

        this.loadingPlaceholderSlots = []
        this.setUrlPageParam(recoveryPage)
        this.resetProducts()
        await this.getProducts('initial', recoveryPage)

        return true
    },
    /**
     * Recover from an Adobe GraphQL out-of-bounds page error.
     *
     * @param {Error} error
     * @returns {Promise<boolean>}
     */
    async recoverFromOutOfBoundsError(error) {
        if (!error?.isOutOfBoundsPageError) {
            return false
        }

        const recoveryPage = Math.max(parseInt(error.maxValidPage, 10) || 1, 1)

        this.productFetchErrorMessage = ''
        this.loadingPlaceholderSlots = []
        this.setUrlPageParam(recoveryPage)
        this.resetProducts()
        await this.getProducts('initial', recoveryPage)

        return true
    }
}

export default liveSearchProductsRequest
